import type { Command, CommandGroupName } from './types';

import { extractTextFromCommandLabel } from './command-label-text';

/**
 * Subsequence scoring algorithm from cmdk (MIT) by Paco Coursey.
 * Source: https://github.com/pacocoursey/cmdk/blob/main/cmdk/src/command-score.ts
 *
 * Inlined because cmdk doesn't expose it in its package.json exports map.
 * The algorithm rewards continuous matches, word-boundary jumps, and
 * penalises character skips — producing monotonic results where typing
 * more characters can only narrow the result set, never widen it.
 */
const SCORE_CONTINUE_MATCH = 1;
const SCORE_SPACE_WORD_JUMP = 0.9;
const SCORE_NON_SPACE_WORD_JUMP = 0.8;
const SCORE_CHARACTER_JUMP = 0.17;
const SCORE_TRANSPOSITION = 0.1;
const PENALTY_SKIPPED = 0.999;
const PENALTY_CASE_MISMATCH = 0.9999;
const PENALTY_NOT_COMPLETE = 0.99;

const IS_GAP_REGEXP = /[\\/_+.#"@([{&]/;
const COUNT_GAPS_REGEXP = /[\\/_+.#"@([{&]/g;
const IS_SPACE_REGEXP = /[\s-]/;
const COUNT_SPACE_REGEXP = /[\s-]/g;
const COUNT_COMPOUND_DELIMITERS_REGEXP = /[\s\\/_+.#"@([{&]/g;
const SHORT_QUERY_MAX_LENGTH = 2;
// cmdk multiplies by 0.17 for every letter it finds mid-word, so a score at or above this means
// each jump landed on a word start. Below it the letters are scattered ("code" in "commit mode").
const WORD_ANCHORED_MINIMUM_SCORE = 0.2;
const STOP_WORDS = new Set(['a', 'an', 'and', 'for', 'in', 'of', 'on', 'the', 'to']);
const TYPO_MIN_LENGTH = 4;
const MAX_SKIPPED_LETTERS = 2;
const NON_WORD_REGEXP = /[^\p{L}\p{N}]+/u;
const ONE_LOWERCASE_WORD_REGEXP = /^[a-z0-9]+$/;
const NUMBER_REGEXP = /^\d+$/;
const WHITESPACE_REGEXP = /\s+/;

type MemoizedResults = Record<string, number>;

function commandScoreInner(
  string: string,
  abbreviation: string,
  lowerString: string,
  lowerAbbreviation: string,
  stringIndex: number,
  abbreviationIndex: number,
  memoizedResults: MemoizedResults,
): number {
  if (abbreviationIndex === abbreviation.length) {
    if (stringIndex === string.length) {
      return SCORE_CONTINUE_MATCH;
    }
    return PENALTY_NOT_COMPLETE;
  }

  const memoizeKey = `${stringIndex},${abbreviationIndex}`;
  if (memoizedResults[memoizeKey] !== undefined) {
    return memoizedResults[memoizeKey];
  }

  const abbreviationChar = lowerAbbreviation.charAt(abbreviationIndex);
  let index = lowerString.indexOf(abbreviationChar, stringIndex);
  let highScore = 0;
  let score = 0;
  let transposedScore = 0;

  while (index >= 0) {
    score = commandScoreInner(
      string,
      abbreviation,
      lowerString,
      lowerAbbreviation,
      index + 1,
      abbreviationIndex + 1,
      memoizedResults,
    );
    if (score > highScore) {
      if (index === stringIndex) {
        score *= SCORE_CONTINUE_MATCH;
      } else if (IS_GAP_REGEXP.test(string.charAt(index - 1))) {
        score *= SCORE_NON_SPACE_WORD_JUMP;
        const wordBreaks = string.slice(stringIndex, index - 1).match(COUNT_GAPS_REGEXP);
        if (wordBreaks && stringIndex > 0) {
          score *= PENALTY_SKIPPED ** wordBreaks.length;
        }
      } else if (IS_SPACE_REGEXP.test(string.charAt(index - 1))) {
        score *= SCORE_SPACE_WORD_JUMP;
        const spaceBreaks = string.slice(stringIndex, index - 1).match(COUNT_SPACE_REGEXP);
        if (spaceBreaks && stringIndex > 0) {
          score *= PENALTY_SKIPPED ** spaceBreaks.length;
        }
      } else {
        score *= SCORE_CHARACTER_JUMP;
        if (stringIndex > 0) {
          score *= PENALTY_SKIPPED ** (index - stringIndex);
        }
      }

      if (string.charAt(index) !== abbreviation.charAt(abbreviationIndex)) {
        score *= PENALTY_CASE_MISMATCH;
      }
    }

    if (
      (score < SCORE_TRANSPOSITION &&
        lowerString.charAt(index - 1) === lowerAbbreviation.charAt(abbreviationIndex + 1)) ||
      (lowerAbbreviation.charAt(abbreviationIndex + 1) ===
        lowerAbbreviation.charAt(abbreviationIndex) &&
        lowerString.charAt(index - 1) !== lowerAbbreviation.charAt(abbreviationIndex))
    ) {
      transposedScore = commandScoreInner(
        string,
        abbreviation,
        lowerString,
        lowerAbbreviation,
        index + 1,
        abbreviationIndex + 2,
        memoizedResults,
      );
      if (transposedScore * SCORE_TRANSPOSITION > score) {
        score = transposedScore * SCORE_TRANSPOSITION;
      }
    }

    if (score > highScore) {
      highScore = score;
    }
    index = lowerString.indexOf(abbreviationChar, index + 1);
  }

  memoizedResults[memoizeKey] = highScore;
  return highScore;
}

function formatInput(string: string): string {
  return string.toLowerCase().replace(COUNT_SPACE_REGEXP, ' ');
}

type ScoreText = {
  original: string;
  formatted: string;
  compact: string;
};

function toScoreText(original: string): ScoreText {
  const formatted = formatInput(original);
  return { original, formatted, compact: formatted.replace(COUNT_COMPOUND_DELIMITERS_REGEXP, '') };
}

function hasUppercase(value: string): boolean {
  return value !== value.toLowerCase();
}

function scoreInOrder(target: ScoreText, query: ScoreText, caseSensitive: boolean): number {
  // Smart case: scoring the lowercased forms skips the case-mismatch penalty, which should only
  // cost something when the query itself has a capital letter.
  const directScore = commandScoreInner(
    caseSensitive ? target.original : target.formatted,
    caseSensitive ? query.original : query.formatted,
    target.formatted,
    query.formatted,
    0,
    0,
    {},
  );

  if (!query.compact || query.compact === query.formatted) {
    return directScore;
  }

  const compactScore = commandScoreInner(
    target.compact,
    query.compact,
    target.compact,
    query.compact,
    0,
    0,
    {},
  );

  return Math.max(directScore, compactScore);
}

export function commandScore(string: string, abbreviation: string, aliases?: string[]): number {
  const withAliases = aliases && aliases.length > 0 ? `${string} ${aliases.join(' ')}` : string;
  return scoreInOrder(
    toScoreText(withAliases),
    toScoreText(abbreviation),
    hasUppercase(abbreviation),
  );
}

function isWithinSingleEdit(candidate: string, searchTerm: string): boolean {
  if (candidate === searchTerm) {
    return true;
  }

  const lengthDifference = Math.abs(candidate.length - searchTerm.length);
  if (lengthDifference > 1) {
    return false;
  }

  if (candidate.length === searchTerm.length) {
    const mismatches: number[] = [];

    for (let index = 0; index < candidate.length; index += 1) {
      if (candidate[index] !== searchTerm[index]) {
        mismatches.push(index);
        if (mismatches.length > 2) {
          return false;
        }
      }
    }

    if (mismatches.length === 1) {
      return true;
    }

    if (mismatches.length === 2) {
      const firstMismatch = mismatches[0];
      const secondMismatch = mismatches[1];
      if (firstMismatch === undefined || secondMismatch === undefined) {
        return false;
      }
      return (
        secondMismatch === firstMismatch + 1 &&
        candidate[firstMismatch] === searchTerm[secondMismatch] &&
        candidate[secondMismatch] === searchTerm[firstMismatch]
      );
    }

    return false;
  }

  const shorter = candidate.length < searchTerm.length ? candidate : searchTerm;
  const longer = candidate.length < searchTerm.length ? searchTerm : candidate;
  let shorterIndex = 0;
  let longerIndex = 0;
  let edits = 0;

  while (shorterIndex < shorter.length && longerIndex < longer.length) {
    if (shorter[shorterIndex] === longer[longerIndex]) {
      shorterIndex += 1;
      longerIndex += 1;
      continue;
    }

    edits += 1;
    if (edits > 1) {
      return false;
    }

    longerIndex += 1;
  }

  if (longerIndex < longer.length) {
    edits += 1;
  }

  return edits <= 1;
}

// One edit against the whole word, or a substitution or swap within the word's first
// `token.length` letters, so a typo in a word still being typed matches too.
function isTypoOf(word: string, token: string): boolean {
  return (
    isWithinSingleEdit(word, token) ||
    (word.length > token.length && isWithinSingleEdit(word.slice(0, token.length), token))
  );
}

function isSubsequence(needle: string, haystack: string): boolean {
  let needleIndex = 0;
  for (
    let haystackIndex = 0;
    haystackIndex < haystack.length && needleIndex < needle.length;
    haystackIndex += 1
  ) {
    if (haystack[haystackIndex] === needle[needleIndex]) {
      needleIndex += 1;
    }
  }
  return needleIndex === needle.length;
}

// A token matches inside a word when it is the word with a letter or two dropped ("setings",
// "slak"). Wider gaps are scattered letters, like "code" in "confidence".
function isInsideOneWord(token: string, word: string): boolean {
  const first = token.charAt(0);
  for (let start = word.indexOf(first); start !== -1; start = word.indexOf(first, start + 1)) {
    let tokenIndex = 1;
    let skipped = 0;
    for (
      let wordIndex = start + 1;
      tokenIndex < token.length && wordIndex < word.length && skipped <= MAX_SKIPPED_LETTERS;
      wordIndex += 1
    ) {
      if (word[wordIndex] === token[tokenIndex]) {
        tokenIndex += 1;
      } else {
        skipped += 1;
      }
    }
    if (tokenIndex === token.length && skipped <= MAX_SKIPPED_LETTERS) {
      return true;
    }
  }
  return false;
}

function isUppercaseLetter(character: string): boolean {
  return character !== character.toLowerCase();
}

/** A label or keyword, normalized once per command object. */
type PreparedText = {
  // Lowercase letters and digits only, so "Log out" meets "logout" and "git-hub" meets "GitHub".
  joined: string;
  // Offsets in `joined` where each word, and each capitalized hump inside a word, starts.
  starts: readonly number[];
  words: readonly string[];
  // First letter of every word and hump: "Open in GitHub" is "oigh".
  initials: string;
  significantInitials: string;
};

type PreparedLabel = PreparedText & {
  score: ScoreText;
  length: number;
};

type PreparedCommand = {
  label: PreparedLabel;
  keywords: readonly PreparedText[];
};

const FIRST_WORD_START: readonly number[] = [0];

function prepareText(text: string): PreparedText {
  // Most keywords are one lowercase word, and the first keystroke prepares every keyword.
  if (ONE_LOWERCASE_WORD_REGEXP.test(text)) {
    const initial = text.charAt(0);
    return {
      joined: text,
      starts: FIRST_WORD_START,
      words: [text],
      initials: initial,
      significantInitials: STOP_WORDS.has(text) ? '' : initial,
    };
  }

  let joined = '';
  const starts: number[] = [];
  const words: string[] = [];
  let initials = '';
  let significantInitials = '';

  for (const word of text.split(NON_WORD_REGEXP)) {
    if (!word) {
      continue;
    }
    const lowerWord = word.toLowerCase();
    const isStopWord = STOP_WORDS.has(lowerWord);
    starts.push(joined.length);
    initials += lowerWord.charAt(0);
    if (!isStopWord) {
      significantInitials += lowerWord.charAt(0);
    }
    if (lowerWord !== word) {
      for (let index = 1; index < word.length; index += 1) {
        if (isUppercaseLetter(word.charAt(index)) && !isUppercaseLetter(word.charAt(index - 1))) {
          starts.push(joined.length + index);
          initials += lowerWord.charAt(index);
          if (!isStopWord) {
            significantInitials += lowerWord.charAt(index);
          }
        }
      }
    }
    words.push(lowerWord);
    joined += lowerWord;
  }

  return { joined, starts, words, initials, significantInitials };
}

const preparedCommands = new WeakMap<Command, PreparedCommand>();

function prepareCommand(command: Command): PreparedCommand {
  const cached = preparedCommands.get(command);
  if (cached) {
    return cached;
  }

  const labelText = extractTextFromCommandLabel(command.label);
  const prepared: PreparedCommand = {
    label: { ...prepareText(labelText), score: toScoreText(labelText), length: labelText.length },
    keywords: (command.keywords ?? []).map(prepareText),
  };
  preparedCommands.set(command, prepared);
  return prepared;
}

type PreparedQuery = {
  score: ScoreText;
  caseSensitive: boolean;
  joined: string;
  isShort: boolean;
  tokens: readonly string[];
  historyKey: string;
};

function toJoined(value: string): string {
  return value.split(NON_WORD_REGEXP).join('').toLowerCase();
}

function toHistoryKey(searchTerm: string): string {
  return searchTerm.trim().toLowerCase().split(WHITESPACE_REGEXP).join(' ');
}

function prepareQuery(searchTerm: string): PreparedQuery {
  const trimmed = searchTerm.trim();
  const tokens = trimmed
    .split(WHITESPACE_REGEXP)
    .map(toJoined)
    .filter((token) => token.length > 0);
  const joined = tokens.join('');
  return {
    score: toScoreText(trimmed),
    caseSensitive: hasUppercase(trimmed),
    joined,
    isShort: joined.length <= SHORT_QUERY_MAX_LENGTH,
    tokens,
    historyKey: toHistoryKey(searchTerm),
  };
}

function startsAtWord(text: PreparedText, token: string): boolean {
  return text.starts.some((start) => text.joined.startsWith(token, start));
}

function isInsideWord(text: PreparedText, token: string): boolean {
  return (
    token.length > SHORT_QUERY_MAX_LENGTH && text.words.some((word) => isInsideOneWord(token, word))
  );
}

function hasTypo(text: PreparedText, token: string): boolean {
  return token.length >= TYPO_MIN_LENGTH && text.words.some((word) => isTypoOf(word, token));
}

function hasInitials(text: PreparedText, query: string): boolean {
  return text.initials.includes(query) || text.significantInitials.includes(query);
}

/** Lower is better. */
const MATCH_TIER = {
  exactLabel: 1,
  // A label prefix, or an exact keyword such as a pull request number typed in full.
  labelPrefix: 2,
  wordPrefix: 3,
  acronym: 4,
  fuzzyLabel: 5,
  keyword: 6,
  typo: 7,
} as const;

type MatchTier = (typeof MATCH_TIER)[keyof typeof MATCH_TIER];

function worseTier(left: MatchTier, right: MatchTier): MatchTier {
  return left > right ? left : right;
}

function betterTier(left: MatchTier | null, right: MatchTier | null): MatchTier | null {
  if (left === null || right === null) {
    return left ?? right;
  }
  return left < right ? left : right;
}

type TokenMatch = {
  tier: MatchTier;
  onLabel: boolean;
};

function matchToken(prepared: PreparedCommand, token: string): TokenMatch | null {
  const { label, keywords } = prepared;
  if (startsAtWord(label, token)) {
    return { tier: MATCH_TIER.wordPrefix, onLabel: true };
  }
  if (isInsideWord(label, token)) {
    return { tier: MATCH_TIER.fuzzyLabel, onLabel: true };
  }
  if (keywords.some((keyword) => startsAtWord(keyword, token) || isInsideWord(keyword, token))) {
    return { tier: MATCH_TIER.keyword, onLabel: false };
  }
  if (hasTypo(label, token)) {
    return { tier: MATCH_TIER.typo, onLabel: true };
  }
  if (keywords.some((keyword) => hasTypo(keyword, token))) {
    return { tier: MATCH_TIER.typo, onLabel: false };
  }
  return null;
}

/** Every token has to match a word of the label or of a keyword, in any order. */
function matchTokens(
  prepared: PreparedCommand,
  tokens: readonly string[],
): { tier: MatchTier; labelCoverage: number } | null {
  let tier: MatchTier = MATCH_TIER.wordPrefix;
  let tokensOnLabel = 0;
  for (const token of tokens) {
    const tokenMatch = matchToken(prepared, token);
    if (!tokenMatch) {
      return null;
    }
    tier = worseTier(tier, tokenMatch.tier);
    tokensOnLabel += tokenMatch.onLabel ? 1 : 0;
  }
  return { tier, labelCoverage: tokensOnLabel / tokens.length };
}

function getWholeQueryTier(prepared: PreparedCommand, query: PreparedQuery): MatchTier | null {
  const { label, keywords } = prepared;
  if (label.joined === query.joined) {
    return MATCH_TIER.exactLabel;
  }
  if (label.joined.startsWith(query.joined)) {
    return MATCH_TIER.labelPrefix;
  }
  if (query.joined === label.significantInitials || query.joined === label.initials) {
    return MATCH_TIER.acronym;
  }
  // One or two letters only count at a word start (the token rules) or within the initials, as
  // "gs" is in Go to Slack integration config, so they don't match stray letters.
  if (!query.isShort) {
    return null;
  }
  if (hasInitials(label, query.joined)) {
    return MATCH_TIER.fuzzyLabel;
  }
  return keywords.some((keyword) => hasInitials(keyword, query.joined)) ? MATCH_TIER.keyword : null;
}

type CommandMatch = {
  tier: MatchTier;
  inOrderScore: number;
  labelCoverage: number;
  isExactKeyword: boolean;
};

function matchCommand(prepared: PreparedCommand, query: PreparedQuery): CommandMatch | null {
  const { label } = prepared;
  const tokenMatch = matchTokens(prepared, query.tokens);
  const isExactKeyword = prepared.keywords.some((keyword) => keyword.joined === query.joined);
  // A word-anchored score needs the first letter at a word start, since any other start costs a
  // character jump, so most labels skip scoring.
  const canScoreInOrder =
    label.initials.includes(query.joined.charAt(0)) && isSubsequence(query.joined, label.joined);
  let tier = betterTier(tokenMatch?.tier ?? null, getWholeQueryTier(prepared, query));
  if (tier === null && !isExactKeyword && (query.isShort || !canScoreInOrder)) {
    return null;
  }

  const score = canScoreInOrder ? scoreInOrder(label.score, query.score, query.caseSensitive) : 0;
  const inOrderScore = score >= WORD_ANCHORED_MINIMUM_SCORE ? score : 0;
  // A longer query can also run across words, as "addrepo" does in Add a repository to cubic.
  if (!query.isShort && inOrderScore > 0) {
    tier = betterTier(tier, MATCH_TIER.fuzzyLabel);
  }
  // An exact keyword confirms a label match, and a number names its command outright, like a pull
  // request typed in full. Otherwise a keyword is an alias, or a category many commands share
  // ("settings"), and must not outrank another command's label.
  const labelMatches = tier !== null && tier <= MATCH_TIER.fuzzyLabel;
  if (isExactKeyword && (labelMatches || NUMBER_REGEXP.test(query.joined))) {
    tier = betterTier(tier, MATCH_TIER.labelPrefix);
  }
  if (tier === null && isExactKeyword) {
    tier = MATCH_TIER.keyword;
  }
  if (tier === null) {
    return null;
  }

  return {
    tier,
    inOrderScore,
    labelCoverage: tokenMatch?.labelCoverage ?? 0,
    isExactKeyword,
  };
}

/**
 * We store usage stats locally for each command:
 * {
 *   "commandId": {
 *       count: number;
 *       lastUsed: number; // timestamp in ms
 *   }
 * }
 */
export type UsageData = {
  [commandId: string]: {
    count: number;
    lastUsed: number;
  };
};

/** Normalized query -> command id -> how often that query led to that command. */
export type QueryHistory = {
  [query: string]: {
    [commandId: string]: {
      uses: number;
      lastUsed: number;
    };
  };
};

type QueryPick = QueryHistory[string][string];

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

// Slack's quick switcher points (slack.engineering/a-faster-smarter-quick-switcher).
const RECENCY_POINTS: readonly { maxAgeMs: number; points: number }[] = [
  { maxAgeMs: 4 * HOUR_MS, points: 100 },
  { maxAgeMs: DAY_MS, points: 80 },
  { maxAgeMs: 3 * DAY_MS, points: 60 },
  { maxAgeMs: 7 * DAY_MS, points: 40 },
  { maxAgeMs: 30 * DAY_MS, points: 20 },
  { maxAgeMs: 90 * DAY_MS, points: 10 },
];
const MAX_RECENCY_POINTS = 100;
const MAX_USAGE_BOOST = 0.15;
const USAGE_COUNT_CAP = 10;
const EXACT_KEYWORD_BONUS = 0.1;
const LABEL_COVERAGE_BONUS = 0.1;
const RECENT_COMMAND_MAX_AGE_MS = 30 * DAY_MS;

// Firefox's adaptive history (firefox-source-docs.mozilla.org/browser/urlbar/ranking.html).
const QUERY_PICK_RETENTION = 0.9;
const QUERY_PICK_DAILY_DECAY = 0.975;
const QUERY_HISTORY_MAX_AGE_MS = 90 * DAY_MS;
const QUERY_HISTORY_MAX_QUERIES = 200;
const LEARNED_WEIGHT_THRESHOLD = 1;

function getRecencyPoints(ageMs: number): number {
  return RECENCY_POINTS.find((bucket) => ageMs <= bucket.maxAgeMs)?.points ?? 0;
}

function getUsageBoost(usage: UsageData[string] | undefined, now: number): number {
  if (!usage) {
    return 0;
  }
  const recency = getRecencyPoints(now - usage.lastUsed) / MAX_RECENCY_POINTS;
  const frequency = Math.min(usage.count, USAGE_COUNT_CAP) / USAGE_COUNT_CAP;
  return (MAX_USAGE_BOOST * recency * (1 + frequency)) / 2;
}

function getDecayedUses(pick: QueryPick, now: number): number {
  const ageMs = now - pick.lastUsed;
  if (ageMs > QUERY_HISTORY_MAX_AGE_MS) {
    return 0;
  }
  return pick.uses * QUERY_PICK_DAILY_DECAY ** Math.max(0, Math.floor(ageMs / DAY_MS));
}

function getLearnedWeights(
  queryHistory: QueryHistory,
  historyKey: string,
  now: number,
): Map<string, number> {
  const weights = new Map<string, number>();
  for (const [query, picks] of Object.entries(queryHistory)) {
    if (!query.startsWith(historyKey)) {
      continue;
    }
    const exactMultiplier = query === historyKey ? 2 : 1;
    for (const [commandId, pick] of Object.entries(picks)) {
      const weight = getDecayedUses(pick, now) * exactMultiplier;
      if (weight > (weights.get(commandId) ?? 0)) {
        weights.set(commandId, weight);
      }
    }
  }
  return weights;
}

export function recordQueryPick(
  queryHistory: QueryHistory,
  searchTerm: string,
  commandId: string,
  now: number,
): QueryHistory {
  const historyKey = toHistoryKey(searchTerm);
  if (!historyKey) {
    return queryHistory;
  }

  const picks = queryHistory[historyKey] ?? {};
  const previous = picks[commandId];
  const uses = (previous ? getDecayedUses(previous, now) : 0) * QUERY_PICK_RETENTION + 1;
  const nextHistory = {
    ...queryHistory,
    [historyKey]: { ...picks, [commandId]: { uses, lastUsed: now } },
  };

  const freshQueries = Object.entries(nextHistory).flatMap(([query, queryPicks]) => {
    const freshPicks = Object.entries(queryPicks).filter(
      ([, pick]) => now - pick.lastUsed <= QUERY_HISTORY_MAX_AGE_MS,
    );
    if (freshPicks.length === 0) {
      return [];
    }
    const lastUsed = Math.max(...freshPicks.map(([, pick]) => pick.lastUsed));
    return [{ query, picks: Object.fromEntries(freshPicks), lastUsed }];
  });

  return Object.fromEntries(
    freshQueries
      .sort((a, b) => b.lastUsed - a.lastUsed)
      .slice(0, QUERY_HISTORY_MAX_QUERIES)
      .map(({ query, picks: queryPicks }) => [query, queryPicks]),
  );
}

export function getRecentCommandIds(
  usageData: UsageData,
  limit: number,
  now = Date.now(),
): string[] {
  return Object.entries(usageData)
    .filter(([, usage]) => now - usage.lastUsed <= RECENT_COMMAND_MAX_AGE_MS)
    .sort(([, a], [, b]) => b.lastUsed - a.lastUsed)
    .slice(0, limit)
    .map(([commandId]) => commandId);
}

export type RankingSignals = {
  usageData: UsageData;
  queryHistory: QueryHistory;
  now?: number;
};

export type RankingOptions = {
  // The group of the page's own commands, such as the open pull request's, which rank first
  // within their match tier.
  boostGroup?: CommandGroupName;
};

type RankedCommand<T> = {
  command: T;
  index: number;
  learnedWeight: number;
  tier: MatchTier;
  isBoosted: boolean;
  score: number;
  labelLength: number;
  priority: number;
};

/**
 * With an empty search, every command by priority. Otherwise only the matches, best first:
 * commands this query (or a longer one starting with it) led to before, then by match tier, then
 * the boosted group, then by score with the usage boost, the shorter label, priority and input
 * order.
 */
export function rankCommands<T extends Command>(
  commands: readonly T[],
  searchTerm: string,
  { usageData, queryHistory, now = Date.now() }: RankingSignals,
  { boostGroup }: RankingOptions = {},
): T[] {
  if (!searchTerm.trim()) {
    return [...commands].sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0));
  }

  const query = prepareQuery(searchTerm);
  if (!query.joined) {
    return [];
  }

  const learnedWeights = getLearnedWeights(queryHistory, query.historyKey, now);
  const ranked: RankedCommand<T>[] = [];
  for (const [index, command] of commands.entries()) {
    const prepared = prepareCommand(command);
    const match = matchCommand(prepared, query);
    if (!match) {
      continue;
    }
    const learnedWeight = learnedWeights.get(command.id) ?? 0;
    ranked.push({
      command,
      index,
      learnedWeight: learnedWeight >= LEARNED_WEIGHT_THRESHOLD ? learnedWeight : 0,
      tier: match.tier,
      isBoosted: boostGroup !== undefined && command.group === boostGroup,
      score:
        match.inOrderScore +
        LABEL_COVERAGE_BONUS * match.labelCoverage +
        (match.isExactKeyword ? EXACT_KEYWORD_BONUS : 0) +
        getUsageBoost(usageData[command.id], now),
      labelLength: prepared.label.length,
      priority: command.priority ?? 0,
    });
  }

  ranked.sort(
    (a, b) =>
      b.learnedWeight - a.learnedWeight ||
      a.tier - b.tier ||
      Number(b.isBoosted) - Number(a.isBoosted) ||
      b.score - a.score ||
      a.labelLength - b.labelLength ||
      b.priority - a.priority ||
      a.index - b.index,
  );

  return ranked.map(({ command }) => command);
}
